// src/api/ops.ts
//
// Admin-only operational reads behind Global Settings → Health Details and
// → Logs (UPM-169). All three endpoints are read-only.
import { apiFetch } from "./api-fetch";

export type CheckStatus = "ok" | "error";

export interface HealthCheck {
  name: "database" | "redis" | "celery_workers" | string;
  status: CheckStatus;
  latency_ms: number | null;
  detail: Record<string, unknown>;
}

export interface BuildInfo {
  /** 12-char commit SHA the image was built from; null outside a deployed build. */
  sha: string | null;
  built_at: string | null;
}

export interface ReportingProcess {
  source: string;
  host: string | null;
  sha: string | null;
  environment: string | null;
  pids: number;
  last_seen: string;
}

export interface AppliedMigration {
  name: string;
  applied_at: string;
  applied_by: string | null;
  /** "applied" = run by db/migrate.py; "marked" = recorded as applied by hand earlier. */
  method: string | null;
}

export interface HealthDetails {
  status: "ok" | "degraded";
  checked_at: string;
  build: BuildInfo;
  process: {
    host: string;
    pid: number;
    environment: string;
    started_at: string;
    uptime_s: number;
    python: string;
  };
  checks: HealthCheck[];
  /** null when the app_logs table isn't there yet. */
  processes: ReportingProcess[] | null;
  migrations: {
    tracking_enabled?: boolean;
    applied_count?: number;
    latest?: AppliedMigration | null;
    unrecorded_files?: string[];
    error?: string;
  };
  logs: {
    enabled_in_this_process: boolean;
    retention_days: number;
    table_present: boolean;
    last_24h: { source: string; level: string; count: number }[];
  };
}

export interface MigrationsResponse {
  tracking_enabled: boolean;
  migrations: AppliedMigration[];
  unrecorded_files: string[];
}

export type LogLevelFilter = "all" | "info" | "warning" | "error";

export interface LogEntry {
  id: number;
  ts: string;
  level: string;
  levelno: number;
  logger: string;
  message: string;
  exc_text: string | null;
  location: string | null;
  source: string;
  host: string | null;
  pid: number | null;
  sha: string | null;
  environment: string | null;
}

export interface LogsResponse {
  table_present: boolean;
  items: LogEntry[];
  next_cursor: string | null;
  enabled_in_this_process: boolean;
  retention_days: number;
}

export interface LogsQuery {
  level?: LogLevelFilter;
  source?: string;
  q?: string;
  since_hours?: number;
  cursor?: string | null;
  limit?: number;
}

function toQuery(params: LogsQuery): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : "";
}

/** The frontend bundle's own build, baked in by the deploy workflow. */
export const frontendBuild: BuildInfo = {
  sha: (import.meta.env?.VITE_BUILD_SHA as string | undefined) || null,
  built_at: (import.meta.env?.VITE_BUILD_TIME as string | undefined) || null,
};

export const opsApi = {
  health: () => apiFetch<HealthDetails>("/admin/health"),
  migrations: () => apiFetch<MigrationsResponse>("/admin/migrations"),
  // URLSearchParams encodes the "+" in the cursor's timestamp offset; a raw
  // "+" in a query string would arrive as a space and break paging.
  logs: (params: LogsQuery) => apiFetch<LogsResponse>(`/admin/logs${toQuery(params)}`),
};
